import { Injectable, makeStateKey, NgModule, TransferState } from "@angular/core";
import { Routes, RouterModule, ActivatedRouteSnapshot, Resolve } from "@angular/router";
import { PageNotFoundComponent } from "./common/components/page-not-found/page-not-found.component";
import { OfferUtilService } from "./common/services/offer-util.service";
import { RouteValidatorService } from "./core/services/route-validator.service";

const IS_SHOWHEADER_KEY = makeStateKey<boolean>('showHeader');
const IS_THROTTLE_KEY = makeStateKey<boolean>('throttle');

@Injectable({ providedIn: 'root' })
export class CoreContentService implements Resolve<any> {
  constructor(private offerUtilService: OfferUtilService, private _transferState: TransferState, private routeValidatorService: RouteValidatorService) { }
  resolve(
    route: ActivatedRouteSnapshot
  ): Promise<any> {
    const variation = route.queryParams['variation'] || '';
    const throttle = (route.queryParams['pdp'] && route.queryParams['pdp'].startsWith('new_')) ? true : null;// NCDDM-14085: Throttling the PDP page old vs new
    this._transferState.set(IS_THROTTLE_KEY, throttle);
    let { bundles, isAEMPage, pageName, category, showHeader } = route.data;
    showHeader = showHeader ?? !this.offerUtilService.isMbolWorkFlow(route.queryParams['flowId']);
    this._transferState.set(IS_SHOWHEADER_KEY, showHeader);
    return this.offerUtilService.loadBundleContent({ ...bundles, aemPages: { "isAEMPage": isAEMPage || false, "pageName": pageName || "", "category": category || "", "variation": variation || "" } }).then(_ => {
      if (!this.routeValidatorService.isValidRoute(route['_routerState'].url)) {
        console.log(">>> Invalid Route !!!");
        return Promise.reject(false);
      }
      // console.log(">>> Valid Route !!!");
      const isPDPMigrated = this.routeValidatorService.isAEMMigratedPage(route['_routerState'].url);
      return pageName != 'pdp' ? Promise.resolve(true) : getPageData(this.offerUtilService, isPDPMigrated, route, variation);
    })
  }
}

async function getPageData(offerUtilService, isPDPMigrated, routeData, variation): Promise<boolean> {
  // console.log(`>>> Get data based on ${isPDPMigrated ? 'NEW AEM' : 'OLD ICMS'} PDP PAGE !!!`,);
  const category = routeData.params['cardName'] || '';
  let { bundles, pageName } = routeData.data;
  (!isPDPMigrated) ? await offerUtilService.loadBundleContent({ isAll: true }) : await offerUtilService.loadBundleContent({...bundles, aemPages: { "isAEMPage": true, "pageName": pageName || "", "category": category || "", "variation": variation || "" } }); // return data based on new AEM PDP page
  return Promise.resolve(true);
}

export const routes: Routes = [
  {
    path: '',
    pathMatch: "full",
    loadComponent: () => import('../app/cards/mpp-redesign/mpp-redesign.component').then(mod => mod.MppRedesignComponent),
    data: { bundles: { isBrand: true }, isAEMPage: true, pageName: 'explore', category: "explore" },
    resolve: { brand: CoreContentService }
  },
  {
    path: 'cititravel/the-reserve-hotel-collection',
    pathMatch: "full",
    loadComponent: () => import('../app/cards/hotel-collection/hotel-collection.component').then(mod => mod.HotelCollectionComponent),
    data: { bundles: { isBrand: true }, showHeader: true, isAEMPage: true, pageName: 'travel', category: "travel" },
    resolve: { brand: CoreContentService }
  },
  {
    path: 'view-all-credit-cards',
    pathMatch: "full",
    loadComponent: () => import('../app/cards/mpp-redesign/mpp-redesign.component').then(mod => mod.MppRedesignComponent),
    data: { bundles: { isBrand: true }, isAEMPage: true, pageName: 'vac', category: "view-all-credit-cards" },
    resolve: { brand: CoreContentService }
  },
  {
    path: 'rewards-credit-cards',
    pathMatch: "full",
    loadComponent: () => import('../app/cards/mpp-redesign/mpp-redesign.component').then(mod => mod.MppRedesignComponent),
    data: { bundles: { isBrand: true }, isAEMPage: true, pageName: 'mpp', category: "rewards" },
    resolve: { brand: CoreContentService }
  },
  {
    path: 'travel-reward-credit-cards',
    pathMatch: "full",
    loadComponent: () => import('../app/cards/mpp-redesign/mpp-redesign.component').then(mod => mod.MppRedesignComponent),
    data: { bundles: { isBrand: true }, isAEMPage: true, pageName: 'mpp', category: "travel" },
    resolve: { brand: CoreContentService }
  },
  {
    path: 'balance-transfer-credit-cards',
    pathMatch: "full",
    loadComponent: () => import('../app/cards/mpp-redesign/mpp-redesign.component').then(mod => mod.MppRedesignComponent),
    data: { bundles: { isBrand: true }, isAEMPage: true, pageName: 'mpp', category: "balance-transfer" },
    resolve: { brand: CoreContentService }
  },
  {
    path: 'savings-and-cash-back-credit-cards',
    pathMatch: "full",
    loadComponent: () => import('../app/cards/mpp-redesign/mpp-redesign.component').then(mod => mod.MppRedesignComponent),
    data: { bundles: { isBrand: true }, isAEMPage: true, pageName: 'mpp', category: "cashback" },
    resolve: { brand: CoreContentService }
  },
  {
    path: 'no-annual-fee-credit-cards',
    pathMatch: "full",
    loadComponent: () => import('../app/cards/mpp-redesign/mpp-redesign.component').then(mod => mod.MppRedesignComponent),
    data: { bundles: { isBrand: true }, isAEMPage: true, pageName: 'mpp', category: "no-annual-fee" },
    resolve: { brand: CoreContentService }
  },
  {
    path: 'retail-store-credit-cards',
    pathMatch: "full",
    loadComponent: () => import('../app/cards/mpp-redesign/mpp-redesign.component').then(mod => mod.MppRedesignComponent),
    data: { bundles: { isBrand: true }, isAEMPage: true, pageName: 'mpp', category: "retail" },
    resolve: { brand: CoreContentService }
  },
  {
    path: 'compare/:cardCategories',
    pathMatch: "full",
    loadChildren: () => import('../app/cards/mpp/multi-product.module').then(m => m.MultiProductsModule),
    data: { bundles: { isAll: true }, showHeader: true },
    resolve: { brand: CoreContentService }
  },
  {
    path: 'refer-a-friend/view-all-cards',
    pathMatch: 'full',
    loadChildren: () => import('../app/cards/raf/raf.module').then(m => m.RafModule),
    data: { bundles: { isAll: true }, showHeader: true },
    resolve: { brand: CoreContentService }
  },
  {
    path: 'compare-credit-cards',
    pathMatch: 'full',
    loadChildren: () => import('../app/cards/compare-credit-cards/compare-credit-cards.module').then(m => m.CompareCreditCardsModule),
    data: { bundles: { isAll: true }, showHeader: true },
    resolve: { brand: CoreContentService }
  },
  {
    path: 'tools',
    pathMatch: "prefix",
    loadChildren: () => import("../app/tools/tools.module").then(m => m.ToolsModule),
    data: { bundles: { isMPP: true }, showHeader: true },
    resolve: { brand: CoreContentService }
  },
  {
    path: 'compare-credit-cards/CMA-PIT',
    pathMatch: 'full',
    loadChildren: () => import('../app/cards/cca/cca.module').then(m => m.CcaModule),
    data: { bundles: { isBrand:true, isMicro: true }, showHeader: true, pageType: 'CMA-PIT' },
    resolve: { brand: CoreContentService }
  },
  {
    path: 'compare-credit-cards/CMA-PIT-ES',
    pathMatch: 'full',
    loadChildren: () => import('../app/cards/cca/cca.module').then(m => m.CcaModule),
    data: { bundles: { isBrand:true, isMicro: true }, showHeader: true, pageType: 'CMA-PIT-ES' },
    resolve: { brand: CoreContentService }
  },
  {
    path: 'sitemap',
    pathMatch: 'full',
    loadChildren: () => import('../app/cards/sitemap/sitemap.module').then(m => m.SitemapModule),
    data: { bundles: { isHP: true }, showHeader: true },
    resolve: { brand: CoreContentService }
  },
  {
    path: 'rfuidmatch',
    pathMatch: 'full',
    loadChildren: () => import('../app/cards/rf-uid/rf-uid.module').then(m => m.RfUidModule),
    data: { bundles: { isBrand: true }, showHeader: false, pageType: 'rfuidmatch' },
    resolve: { brand: CoreContentService }
  },
  {
    path: 'citi-aa-compare',
    pathMatch: 'full',
    loadChildren: () => import('../app/cards/citi-aa-microsite/citi-aa-microsite.module').then(m => m.CitiAaMicrositeModule),
    data: { bundles: { isMicro: true }, showHeader: false, pageType: 'citi-aa-compare' },
    resolve: { brand: CoreContentService }
  },
  {
    path: 'citi-aa-exec',
    pathMatch: 'full',
    loadChildren: () => import('../app/cards/citi-aa-microsite/citi-aa-microsite.module').then(m => m.CitiAaMicrositeModule),
    data: { bundles: { isMicro: true }, showHeader: false, pageType: 'citi-aa-exec' },
    resolve: { brand: CoreContentService }
  },
  {
    path: 'citi-aa-mileUp-card',
    pathMatch: 'full',
    loadChildren: () => import('../app/cards/citi-aa-microsite/citi-aa-microsite.module').then(m => m.CitiAaMicrositeModule),
    data: { bundles: { isMicro: true }, showHeader: false, pageType: 'citi-aa-mileup-card' },
    resolve: { brand: CoreContentService }
  },
  {
    path: 'citi-aa-plat-elite',
    pathMatch: 'full',
    loadChildren: () => import('../app/cards/citi-aa-microsite/citi-aa-microsite.module').then(m => m.CitiAaMicrositeModule),
    data: { bundles: { isMicro: true }, showHeader: false, pageType: 'citi-aa-plat-elite' },
    resolve: { brand: CoreContentService }
  },
  {
    path: 'citibus-aa-plat-select',
    pathMatch: 'full',
    loadChildren: () => import('../app/cards/citi-aa-microsite/citi-aa-microsite.module').then(m => m.CitiAaMicrositeModule),
    data: { bundles: { isMicro: true }, showHeader: false, pageType: 'citibus-aa-plat-select' },
    resolve: { brand: CoreContentService }
  },
  {
    path: 'citishop/:pageType',
    pathMatch: 'full',
    loadChildren: () => import('../app/cards/citi-shop/citi-shop.module').then(m => m.CitiShopModule),
    data: { bundles: { isDigital: true }, showHeader: true },
    resolve: { brand: CoreContentService }
  },
  {
    path: 'digital-wallet/:walletType',
    pathMatch: 'full',
    loadChildren: () => import('../app/cards/digital-wallet/digital-wallet.module').then(m => m.DigitalWalletModule),
    data: { bundles: { isDigital: true }, showHeader: true },
    resolve: { brand: CoreContentService }
  },
  {
    path: 'dividend-quarterly-offer',
    pathMatch: 'full',
    loadChildren: () => import('../app/cards/dividend-quarterly-offer/dividend-quarterly-offer.module').then(m => m.DividendQuarterlyOfferModule),
    data: { bundles: { isMicro: true }, showHeader: false },
    resolve: { brand: CoreContentService }
  },
  {
    path: 'citi-costco-credit-card-conversion',
    pathMatch: 'full',
    loadChildren: () => import('../app/cards/credit-card-conversion/credit-card-conversion.module').then(m => m.CreditCardConversionModule),
    data: { bundles: { isMicro: true }, showHeader: false },
    resolve: { brand: CoreContentService }
  },
  {
    path: 'credit-card-vehicle-form',
    pathMatch: 'full',
    loadChildren: () => import('../app/cards/drivers-edge/drivers-edge.module').then(m => m.DriversEdgeModule),
    data: { bundles: { isBrand:true, isMicro: true }, showHeader: true },
    resolve: { brand: CoreContentService }
  },
  {
    path: "page-not-found",
    component: PageNotFoundComponent,
    data: { bundles: { isBrand:true, isMPP: true }, showHeader: true, isAEMPage: false, pageName: '404' },
    resolve: { brand: CoreContentService },
  },
  {
    path: ':cardName/additional-information',
    loadComponent: () => import('../app/cards/product-detail/product-detail.component').then(m => m.ProductDetailComponent),
    data: { bundles: { isBrand:true }, isAEMPage: false, pageName: 'pdp', isAdditionalInfo: true },
    resolve: { brand: CoreContentService },
  },
  {
    path: ':cardName',
    loadComponent: () => import('../app/cards/product-detail/product-detail.component').then(m => m.ProductDetailComponent),
    data: { bundles: { isBrand:true }, isAEMPage: false, pageName: 'pdp' },
    resolve: { brand: CoreContentService },
  }
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      initialNavigation: "enabledBlocking",
      scrollPositionRestoration: "top",
      onSameUrlNavigation: "ignore",
    })
  ],
  exports: [RouterModule]
})
export class CitibankRoutingModule {
}
